import { useState, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonLink } from '@components/shared/V2Button';
import routes from '@root/routes';
import { SearchListing, StoreState } from '@root/types';
import api from '@shared/api';
import actions from '@store/actions/homepage/analyticsActions';
import { hasCurrentUserLoaded } from '@store/selectors';
import s from './Homepage.module.less';
import ListingCardCarousel from './ListingCardCarousel';

const globalDefaultMarketSlug = 'new_york_city';

const ExclusiveListingsSection = ({ selectedMarket }) => {
  const flags = useFlags();
  const { t } = useTranslation('homepage');
  const [exclusiveListingsData, setExclusiveListingsData] = useState<{
    loading: boolean;
    listings: SearchListing[];
  }>({
    loading: true,
    listings: [],
  });
  const userHasLoaded = useSelector(hasCurrentUserLoaded);
  const currentUser = useSelector((state: StoreState) => state.currentUser);
  const homeRedesignFlag = flags['homepage-redesign'];

  const listingMarketSlug = currentUser?.marketSlug || globalDefaultMarketSlug;
  const marketSlug = homeRedesignFlag ? selectedMarket.value : listingMarketSlug;
  let firstToMarketLink = '/office-space/new-york-city-ny?exclusive=true';

  if (homeRedesignFlag) {
    firstToMarketLink = `/search/${marketSlug}?exclusive=true`;
  } else if (currentUser) {
    firstToMarketLink = '/office-space/for-rent?exclusive=true';
  }

  const fetchExclusiveListings = async () => {
    if (!userHasLoaded && !homeRedesignFlag) return;

    const response = await api.fetch(routes.api.homepage.exclusiveListings(marketSlug));
    const listings = await response.json();
    setExclusiveListingsData({ loading: false, listings });
  };

  useEffect(() => {
    fetchExclusiveListings();
  }, [selectedMarket, userHasLoaded]);

  const showExclusiveListings =
    userHasLoaded && !exclusiveListingsData.loading && exclusiveListingsData.listings.length >= 3;

  const dispatch = useDispatch();
  const handleFirstToMarketCarouselPaginated = () => {
    dispatch(actions.firstToMarketCarouselPaginated());
  };

  const handleFirstToMarketCarouselCardClicked = () => {
    dispatch(actions.firstToMarketCarouselCardClicked());
  };

  const handleClickViewAllExclusive = () => {
    dispatch(actions.viewAllExclusiveListingsClicked());
  };

  if (!showExclusiveListings) return null;

  return (
    <div className={s.firstToMarketListings} data-testid="exclusiveListingsSection">
      <div className={s.firstToMarketListingsResponsiveWidth}>
        <Tooltip
          overlayClassName={s.tooltip}
          trigger="hover"
          placement="leftBottom"
          title={t('exclusiveListings.ftmTooltip')}
        >
          <h2 className={s.title}>{t('exclusiveListings.title')}</h2>
        </Tooltip>
        <ListingCardCarousel
          listings={exclusiveListingsData.listings}
          loggedInUser={!!currentUser}
          onClick={handleFirstToMarketCarouselCardClicked}
          sliderAfterChange={handleFirstToMarketCarouselPaginated}
        />
        <div className={s.exclusiveListingCtaPositioner}>
          <ButtonLink
            className={s.exclusiveListingCta}
            type="primary"
            size="large"
            href={firstToMarketLink}
            onClick={handleClickViewAllExclusive}
          >
            {t('exclusiveListings.cta')}
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

export default ExclusiveListingsSection;
